import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import CitationAnalytics from '../components/analytics/CitationAnalytics';
import { 
  Button, Box, Alert, CircularProgress, Typography, Paper, 
  Pagination
} from '@mui/material';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog } from '@fortawesome/free-solid-svg-icons';
import { getCitationSettings, getCitationAnalytics, getRecentCitationsPaginated } from '../services/api';

const ROWS_PER_PAGE = 10;

const CitationAnalyticsPage: React.FC = () => {
  const clientId = localStorage.getItem('client_id');
  const navigate = useNavigate();

  // State for core analytics
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [citationsEnabled, setCitationsEnabled] = useState(false);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);

  // State for paginated recent citations
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [recentCitations, setRecentCitations] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [recentError, setRecentError] = useState<string | null>(null);

  // Fetch core analytics and settings
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoadingAnalytics(true);
      setAnalyticsError(null);
      if (!clientId) {
        setCitationsEnabled(false);
        setLoadingAnalytics(false);
        return;
      }
      try {
        // Fetch settings and core analytics in parallel
        const [settingsRes, analyticsRes] = await Promise.all([
          getCitationSettings(clientId),
          getCitationAnalytics(clientId)
        ]);

        // Process settings
        if (settingsRes.success) {
          setCitationsEnabled(settingsRes.citations_enabled);
        } else {
          console.error('[DEBUG] Failed to get citation settings:', settingsRes);
          setAnalyticsError('Note: Unable to verify citation status.');
          setCitationsEnabled(false);
        }

        // Process core analytics
        if (analyticsRes.success) {
           setAnalyticsData(analyticsRes);
        } else {
           console.error('[DEBUG] Failed to get core analytics:', analyticsRes);
           if (!analyticsError) setAnalyticsError('Failed to load core analytics data.');
           setAnalyticsData(null);
        }

      } catch (error) {
        console.error('[DEBUG] Error fetching initial data:', error);
        setAnalyticsError('An error occurred loading analytics data.');
        setCitationsEnabled(false);
        setAnalyticsData(null);
      } finally {
        setLoadingAnalytics(false);
      }
    };
    fetchInitialData();
  }, [clientId]);

  // Fetch paginated recent citations when page or clientId changes
  const fetchRecentCitations = useCallback(async (page: number) => {
     if (!clientId || !citationsEnabled) {
         setRecentCitations([]);
         setTotalPages(0);
         setTotalCount(0);
         return;
     }
     setLoadingRecent(true);
     setRecentError(null);
     try {
        const recentRes = await getRecentCitationsPaginated(clientId, page, ROWS_PER_PAGE);
        if (recentRes.success) {
           setRecentCitations(recentRes.data || []);
           setTotalPages(recentRes.pagination?.total_pages || 0);
           setTotalCount(recentRes.pagination?.total_count || 0);
           setCurrentPage(recentRes.pagination?.current_page || page);
        } else {
           console.error('[DEBUG] Failed to get recent citations:', recentRes);
           setRecentError(recentRes.message || 'Failed to load recent queries.');
           setRecentCitations([]);
           setTotalPages(0);
            setTotalCount(0);
        }
     } catch (error) {
        console.error('[DEBUG] Error fetching recent citations:', error);
        setRecentError('An error occurred loading recent queries.');
        setRecentCitations([]);
        setTotalPages(0);
        setTotalCount(0);
     } finally {
        setLoadingRecent(false);
     }
  }, [clientId, citationsEnabled]);

  // Fetch recent citations initially and when page changes
  useEffect(() => {
     fetchRecentCitations(currentPage);
  }, [currentPage, fetchRecentCitations]);

  // Improved page change handler to ensure we only pass the numeric value
  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    // Safely update the page number
    if (typeof value === 'number') {
      setCurrentPage(value);
    }
  };

  // Combined loading state
  const isLoading = loadingAnalytics;
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <CircularProgress />
      </div>
    );
  }
  
  const pageTitle = "Citation Analytics";
  
  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <Typography variant="h4">Citation Analytics</Typography>
        <Box>
          <Button 
            component={Link} 
            to="/settings" 
            variant="outlined" 
            color="primary" 
            startIcon={<FontAwesomeIcon icon={faCog} />}
          >
            Settings
          </Button>
        </Box>
      </div>
      
      <Paper className="bg-white rounded-lg shadow p-6">
        {analyticsError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {analyticsError}
          </Alert>
        )}

        {!clientId ? (
           <Typography>Client ID not found. Please ensure you are logged in or provide a client ID.</Typography>
        ) : citationsEnabled && analyticsData ? (
          <CitationAnalytics 
             clientId={clientId} 
             analyticsData={analyticsData}
             recentCitations={recentCitations}
             loadingRecent={loadingRecent}
             recentError={recentError}
             currentPage={currentPage}
             totalPages={totalPages}
             onPageChange={handlePageChange} 
             totalRecentQueryCount={totalCount}
          />
        ) : !citationsEnabled && clientId ? (
           <div className="flex flex-col items-center justify-center py-16">
             <div className="text-gray-500 text-center max-w-md">
               <div className="text-xl font-medium mb-3">Citations Not Enabled</div>
               <div className="text-sm mb-4">Citation analytics are not available because citations are disabled for this client.</div>
               <div className="text-sm">Enable citations to view detailed analytics, source tracking, and citation performance metrics.</div>
             </div>
           </div>
        ) : (
           <Typography>Could not load analytics data. Please try again later.</Typography>
        )}
      </Paper>
    </div>
  );
};

export default CitationAnalyticsPage;

 